import { useState, useCallback, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Wand2, History, Image as ImageIcon, Loader2, AlertTriangle, Crown, Sparkles, X } from 'lucide-react'
import { ImageUnderstandingModal } from '../components/ImageUnderstandingModal'
import { ImageResultPanel } from '../components/image/ImageResultPanel'
import { useImageGeneration } from '../hooks/useImageGeneration'
import { useAuthStore } from '../stores/auth'

export function ImageGenerator() {
  const navigate = useNavigate()
  const { quota, isAuthenticated, checkAuth } = useAuthStore()
  const [prompt, setPrompt] = useState('')
  const [showImageUnderstanding, setShowImageUnderstanding] = useState(false)
  const [promptOptimizationModel, setPromptOptimizationModel] = useState<'replicate-openai'>('replicate-openai')
  const [selectedModel, setSelectedModel] = useState<string>('black-forest-labs/flux-schnell')
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [concurrency, setConcurrency] = useState<1 | 2 | 4>(1)
  const [previewImage, setPreviewImage] = useState<{ url: string; prompt: string } | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewImage) {
        setPreviewImage(null)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [previewImage])

  const availableModels = [
    { value: 'black-forest-labs/flux-schnell', label: 'black-forest-labs/flux-schnell (默认)' },
    { value: 'prunaai/p-image', label: 'prunaai/p-image' },
    { value: 'prunaai/z-image-turbo', label: 'prunaai/z-image-turbo' }
  ]

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth')
      return
    }
    console.log('ImageGenerator loaded, quota refresh disabled for debugging')
  }, [isAuthenticated, navigate])

  useEffect(() => {
    const checkTokenValidity = async () => {
      if (isAuthenticated) {
        try {
          await checkAuth()
        } catch (error) {
          console.warn('Token validation failed:', error)
          navigate('/auth')
        }
      }
    }
    checkTokenValidity()
  }, [])

  useEffect(() => {
    const savedSettings = localStorage.getItem('multimodal-settings')
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings)
        if (parsed.promptOptimizationModel) setPromptOptimizationModel(parsed.promptOptimizationModel)
        if (parsed.selectedImageModel) setSelectedModel(parsed.selectedImageModel)
      } catch (error) {
        console.error('Failed to load settings:', error)
      }
    }
  }, [])

  useEffect(() => {
    const savedSettings = localStorage.getItem('multimodal-settings')
    let settings = {}
    if (savedSettings) {
      try { settings = JSON.parse(savedSettings) } catch (error) { console.error('Failed to parse settings:', error) }
    }
    localStorage.setItem('multimodal-settings', JSON.stringify({ ...settings, selectedImageModel: selectedModel }))
  }, [selectedModel])

  const handlePromptGenerated = useCallback((generatedPrompt: string) => {
    setPrompt(generatedPrompt)
  }, [])

  const {
    generateImage, generateBatchImages, isGenerating, progress,
    currentBatchImages, generatedImages, error
  } = useImageGeneration()

  const handleOptimizePrompt = useCallback(async (prompt: string): Promise<string> => {
    const { apiClient } = await import('../utils/api')
    const response = await apiClient.optimizePrompt(prompt, promptOptimizationModel)
    return response.optimized_prompt || prompt
  }, [promptOptimizationModel])

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return
    if (concurrency === 1) {
      await generateImage(prompt, selectedModel)
    } else {
      await generateBatchImages(prompt, concurrency, selectedModel)
    }
  }, [prompt, concurrency, selectedModel, generateImage, generateBatchImages])

  const handleOptimize = useCallback(async () => {
    if (!prompt.trim()) return
    setIsOptimizing(true)
    try {
      const optimizedPrompt = await handleOptimizePrompt(prompt)
      setPrompt(optimizedPrompt)
    } catch (error) {
      console.error('Failed to optimize prompt:', error)
    } finally {
      setIsOptimizing(false)
    }
  }, [prompt, handleOptimizePrompt])

  const handleDownload = useCallback(async (imageUrl: string, filename?: string) => {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename || `generated-image-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Failed to download image:', error)
      window.open(imageUrl, '_blank')
    }
  }, [])

  const handleBatchDownload = useCallback(async () => {
    if (currentBatchImages.length === 0) return
    for (let i = 0; i < currentBatchImages.length; i++) {
      const image = currentBatchImages[i]
      if (i > 0) await new Promise(resolve => setTimeout(resolve, 300))
      await handleDownload(image.url, `generated-batch-${i + 1}-${image.id}.png`)
    }
  }, [currentBatchImages, handleDownload])

  const hasEnoughQuota = () => {
    if (!quota) return true
    return quota.remaining >= concurrency
  }

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* Header */}
      <header className="bg-[#161b22] border-b border-cyan-900/30 px-6 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">DreamCanvas</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={() => setShowImageUnderstanding(true)} className="text-gray-400 hover:text-white transition-colors" title="图片理解">
              <ImageIcon className="w-5 h-5" />
            </button>
            <Link to="/history?media_type=image" className="text-gray-400 hover:text-white transition-colors" title="历史记录">
              <History className="w-5 h-5" />
            </Link>
            <Link to="/" className="px-4 py-2 border border-cyan-900/30 rounded-lg text-gray-300 hover:bg-cyan-900/20 transition-colors font-medium">
              Homepage
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left Panel - Input & Controls */}
            <div className="bg-[#161b22] rounded-2xl p-8 border border-cyan-900/30">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Wand2 className="w-6 h-6 text-cyan-400" />
                  <h2 className="text-2xl font-bold text-white">Generate</h2>
                </div>
                <span className="px-3 py-1 bg-cyan-900/30 text-cyan-400 rounded-full text-sm font-semibold">v2.1 Turbo</span>
              </div>

              <div className="space-y-6">
                {/* Model Selector */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">MODEL</label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full px-4 py-3 bg-[#0d1117] border border-cyan-900/30 rounded-xl text-white font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    {availableModels.map((model) => (
                      <option key={model.value} value={model.value}>{model.label}</option>
                    ))}
                  </select>
                </div>

                {/* Prompt Input */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-300 uppercase tracking-wider">PROMPT</label>
                    <span className="text-sm text-gray-500">{prompt.length} chars</span>
                  </div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe your imagination... (e.g., A futuristic city with flying cars at sunset, cyberpunk style)"
                    className="w-full px-4 py-3 bg-[#0d1117] border border-cyan-900/30 rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-h-[160px]"
                  />
                  <button onClick={() => setPrompt('')} className="mt-2 text-gray-500 hover:text-gray-300 transition-colors text-sm">
                    <span className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  </button>
                </div>

                {/* Quota Warning */}
                {quota && quota.remaining <= 1 && (
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-center space-x-2 text-yellow-400">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">配额不足</span>
                    </div>
                    <p className="text-sm text-yellow-300 mt-1">您的配额即将用完。剩余 {quota.remaining} 次生成机会。</p>
                  </div>
                )}

                {error && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                    <p className="text-red-400">{error}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleOptimize}
                    disabled={isOptimizing || !prompt.trim()}
                    className="flex-1 flex items-center justify-center space-x-2 px-5 py-3 bg-cyan-900/20 border-2 border-cyan-900/50 hover:border-cyan-500/50 hover:bg-cyan-900/30 rounded-xl font-semibold text-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-cyan-900/50 disabled:hover:bg-cyan-900/20"
                  >
                    {isOptimizing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                    <span>Optimize</span>
                  </button>
                  <select
                    value={concurrency}
                    onChange={(e) => setConcurrency(Number(e.target.value) as 1 | 2 | 4)}
                    className="px-5 py-3 bg-cyan-900/20 border-2 border-cyan-900/50 hover:border-cyan-500/50 rounded-xl font-semibold text-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer transition-all"
                  >
                    <option value={1}>1 Image</option>
                    <option value={2}>2 Images</option>
                    <option value={4}>4 Images</option>
                  </select>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim() || !hasEnoughQuota()}
                  className="w-full flex items-center justify-center space-x-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-700 disabled:to-gray-700 rounded-xl font-bold text-lg text-white transition-all disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-6 h-6 animate-spin" /><span>Generating...</span></>
                  ) : !hasEnoughQuota() ? (
                    <><Crown className="w-6 h-6" /><span>Insufficient Quota</span></>
                  ) : (
                    <><Wand2 className="w-6 h-6" /><span>Generate Image</span></>
                  )}
                </button>
              </div>
            </div>

            {/* Right Panel - Results */}
            <ImageResultPanel
              isGenerating={isGenerating}
              progress={progress}
              currentBatchImages={currentBatchImages}
              generatedImages={generatedImages}
              concurrency={concurrency}
              hasEnoughQuota={hasEnoughQuota()}
              onDownload={handleDownload}
              onBatchDownload={handleBatchDownload}
              onPreview={(url, prompt) => setPreviewImage({ url, prompt })}
              onSetPrompt={setPrompt}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      <ImageUnderstandingModal
        isOpen={showImageUnderstanding}
        onClose={() => setShowImageUnderstanding(false)}
        onPromptGenerated={handlePromptGenerated}
      />

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={previewImage.url}
            alt={previewImage.prompt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}
